import { useState } from 'react';
import WebApp from '@twa-dev/sdk';

interface Props {
  onBack: () => void;
}

interface RecoveryStage {
  num: number;
  title: string;
  duration: string;
  goal: string;
  tasks: string[];
  next: string;
}

const STAGES: RecoveryStage[] = [
  {
    num: 1,
    title: 'Acute (급성)',
    duration: '0~3일',
    goal: '통증·붓기 제어',
    tasks: [
      'RICE 적용 (Rest, Ice, Compression, Elevation)',
      '운동 중단',
      '하루 3회 얼음찜질 15~20분',
      '필요 시 진통제 (의사 상담)',
    ],
    next: '통증 5/10 이하 + 붓기 감소 → Phase 2',
  },
  {
    num: 2,
    title: 'Sub-acute (회복기)',
    duration: '4~10일',
    goal: '가동성 회복',
    tasks: [
      '통증 없는 ROM에서만 가동',
      '저강도 가동성 운동 (PNF stretching)',
      '주변 근육 활성화 (isometric)',
      '얼음 → 따뜻한 찜질 전환',
    ],
    next: '관절 가동 80% 회복 → Phase 3',
  },
  {
    num: 3,
    title: 'Strength (재강화)',
    duration: '2~4주',
    goal: '근력·균형 복구',
    tasks: [
      '대체 운동 (저충격, /briefing 참조)',
      '주변 근군 균형 강화',
      '움직임 패턴 재학습',
      '강도는 평소 50~70%',
    ],
    next: '평소 강도 70% 통증 없이 수행 → Phase 4',
  },
  {
    num: 4,
    title: 'Return to Sport (복귀)',
    duration: '4~8주',
    goal: '경기·훈련 복귀',
    tasks: [
      '점진적 부하 증가 (주 10%)',
      '스포츠 specific 동작',
      '컨디션 일일 모니터링 (/recovery)',
      '재발 신호 시 즉시 코치 연락',
    ],
    next: '평소 강도 100% 통증 없이 수행 → 복귀 완료',
  },
];

export function InjuryRecovery({ onBack }: Props) {
  const [currentStage, setCurrentStage] = useState(1);
  const [completed, setCompleted] = useState<Record<number, boolean[]>>({
    1: STAGES[0]!.tasks.map(() => false),
    2: STAGES[1]!.tasks.map(() => false),
    3: STAGES[2]!.tasks.map(() => false),
    4: STAGES[3]!.tasks.map(() => false),
  });

  const stage = STAGES[currentStage - 1]!;

  function toggleTask(i: number) {
    WebApp.HapticFeedback?.selectionChanged?.();
    setCompleted((c) => ({
      ...c,
      [currentStage]: (c[currentStage] ?? []).map((v, j) => (j === i ? !v : v)),
    }));
  }

  const stageProgress = (completed[currentStage] ?? []).filter(Boolean).length;
  const stageTotal = stage.tasks.length;

  return (
    <div className="app">
      <button className="back-link" onClick={onBack}>← 홈</button>
      <h1>🩹 부상 회복 4단계</h1>

      <div style={{ display: 'flex', gap: 6, marginTop: 16, marginBottom: 16 }}>
        {STAGES.map((s) => (
          <button
            key={s.num}
            className={`btn ${s.num === currentStage ? '' : 'btn-secondary'}`}
            onClick={() => setCurrentStage(s.num)}
            style={{ fontSize: 12, padding: 10 }}
          >
            Phase {s.num}
          </button>
        ))}
      </div>

      <div style={{ background: 'var(--tg-secondary)', borderRadius: 12, padding: 16, marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
          <strong>Phase {stage.num} — {stage.title}</strong>
          <small style={{ color: 'var(--tg-hint)' }}>{stage.duration}</small>
        </div>
        <p style={{ fontSize: 14, color: 'var(--tg-hint)' }}>🎯 목표: {stage.goal}</p>
        <div className="progress" style={{ marginTop: 12 }}>
          <div className="progress-bar" style={{ width: `${(stageProgress / stageTotal) * 100}%` }} />
        </div>
        <small style={{ color: 'var(--tg-hint)', marginTop: 4, display: 'block' }}>
          {stageProgress}/{stageTotal} 완료
        </small>
      </div>

      <h3 style={{ marginBottom: 12 }}>이번 phase 체크리스트</h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {stage.tasks.map((t, i) => {
          const isChecked = completed[currentStage]?.[i] ?? false;
          return (
            <button
              key={i}
              className="skill-card"
              onClick={() => toggleTask(i)}
              style={{ padding: 12, opacity: isChecked ? 1 : 0.7 }}
            >
              <span className="icon" style={{ fontSize: 20 }}>{isChecked ? '✅' : '⬜'}</span>
              <div className="meta">
                <h3 style={{ fontSize: 14 }}>{t}</h3>
              </div>
            </button>
          );
        })}
      </div>

      <div style={{ background: 'var(--tg-secondary)', borderRadius: 12, padding: 16, marginTop: 16 }}>
        <strong style={{ fontSize: 14 }}>다음 단계 진입 조건</strong>
        <p style={{ fontSize: 13, color: 'var(--tg-hint)', marginTop: 4 }}>{stage.next}</p>
      </div>
    </div>
  );
}
